// v2.4.0 — DeliveryMapLeaflet : implémentation Leaflet (fallback historique).
// v2.18.4d — Mirror du HUD collapsible (composant partagé DeliveryHUD) pour
// robustesse en environnement headless / sans WebGL. Testids identiques à Mapbox :
// `delivery-hud-card`, `delivery-hud-collapse-btn`, `delivery-hud-pill`.

import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import useOSRMRoute from "../hooks/useOSRMRoute";
import DeliveryHUD from "@/components/DeliveryHUD";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const RED_ICON = L.divIcon({
  className: "civ-deliv-marker-red",
  html: '<div style="width:24px;height:24px;border-radius:50%;background:#dc2626;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>',
  iconSize: [24, 24],
});
const BLUE_ICON = L.divIcon({
  className: "civ-deliv-marker-blue",
  html: '<div style="width:24px;height:24px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.4);animation:civdelivpulse 1.5s infinite"></div>',
  iconSize: [24, 24],
});

function FollowControl({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.panTo(pos);
  }, [pos, map]);
  return null;
}

function MapRefHolder({ onReady }) {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  return null;
}

export default function DeliveryMapLeaflet({ dest, destCode, destLabel, pos, distance, arrived, outOfCI }) {
  const route = useOSRMRoute(pos, dest);
  const etaSec = route.duration_s;
  const remainingM = useMemo(() => {
    if (distance != null) return distance;
    return route.distance_m;
  }, [distance, route.distance_m]);

  // Recentrer = setView sur dest.
  let mapRef = null;
  const onMapReady = (m) => { mapRef = m; };
  const recenter = () => {
    if (mapRef && dest) mapRef.setView(dest, 16);
  };

  // Polyline OSRM si disponible, sinon ligne droite.
  const polyPoints = route.coords && route.coords.length > 1
    ? route.coords.map(([lng, lat]) => [lat, lng])
    : (pos && dest ? [pos, dest] : null);

  return (
    <div className="civ-deliv-page" data-testid="delivery-page" data-renderer="leaflet">
      {outOfCI && (
        <div data-testid="out-of-ci-banner" style={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
          background: "rgba(245,158,11,0.95)", color: "#0F172A",
          padding: "10px 16px", fontSize: 12, fontWeight: 600, textAlign: "center",
          borderBottom: "2px solid #B45309",
        }}>
          📡 Tu n'es pas en Côte d'Ivoire — l'itinéraire part du Plateau d'Abidjan. Ouvre cette page depuis ton mobile en CI pour un trajet réel.
        </div>
      )}
      <a href="/" className="civ-deliv-back" data-testid="delivery-back" style={{
        position: "absolute", top: outOfCI ? 56 : 16, left: 16, zIndex: 5,
        background: "rgba(15,23,42,0.85)", color: "#fff",
        padding: "8px 14px", borderRadius: 999, textDecoration: "none",
        fontWeight: 600, fontSize: 14, border: "1px solid rgba(255,255,255,0.15)",
        backdropFilter: "blur(8px)",
      }}>← Chat</a>

      <div className="civ-deliv-map-wrap" data-testid="delivery-map-wrap" style={{ position: "absolute", inset: 0 }}>
        {dest && (
          <MapContainer center={pos || dest} zoom={16} style={{ width: "100%", height: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            <Marker position={dest} icon={RED_ICON} />
            {pos && <Marker position={pos} icon={BLUE_ICON} />}
            {polyPoints && <Polyline positions={polyPoints} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.7, dashArray: route.status === "ok" ? null : "10,8" }} />}
            {pos && <FollowControl pos={pos} />}
            <MapRefHolder onReady={onMapReady} />
          </MapContainer>
        )}
      </div>

      {/* HUD livreur — v2.18.4d composant partagé (Mapbox + Leaflet). */}
      <DeliveryHUD
        engine="leaflet"
        remainingM={remainingM}
        etaSec={etaSec}
        routeStatus={route.status}
        destCode={destCode}
        destLabel={destLabel}
        arrived={arrived}
        onRecenter={recenter}
        engineFootnote="Leaflet · OpenStreetMap"
      />
    </div>
  );
}
